import { inject, observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { isEmpty } from '@/utils';
import ExtraBindServiceConfig from '@/components/ExtraBindService/data.config';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { formatPriceThousands } from '@/utils/configs/price.config';
import { getGiftsIntro } from '@/utils/configs/gifts.config';
import ThirdPartyPlatformConfig from '@/utils/configs/ThirdPartyPlatform.config';
import { ProductTracker } from '@/utils/Tracker';
import ProfessionalDiscountConfig from './components/ProfessionalDiscountEntry/config';
import styles from './index.module.less';
import useCustomerService from './hooks/useCustomerService';
import useWatch from '@/utils/hooks/useWatch';

// const TradeupEntry = dynamic(import('./components/TradeupEntry'));
const ProfessionalDiscountEntry = dynamic(import('./components/ProfessionalDiscountEntry'));
const CountDownProduct = dynamic(import('@/components/activity/CountDownProduct'));
const PrimeDiscountText = dynamic(import('./components/PrimeDiscountText'));
const PrimeDeliveryText = dynamic(import('./components/PrimeDeliveryText'));

const DISCOUNT_ICON = [
  'https://res.insta360.com/static/668a0dd6910367c6c1bfa96cd88602fd/1.svg',
  'https://res.insta360.com/static/f0b9dfd35c6c4415226740870f0d8103/Frame.svg',
  'https://res.insta360.com/static/3f140461dca2f73766e48fd173ce2cc1/5.svg',
  'https://res.insta360.com/static/0747e951daaf5bb628c3cebef99dd526/ts.svg',
  'https://res.insta360.com/static/79a3ed42f71e84a57a666f63caf1b604/info_FILL1_wght400_GRAD0_opsz24%206.svg',
];
const X4_PM_BUNDLE_CAMERA = 'https://res.insta360.com/static/8ad0e2a724840a7c018f841fdccd0c88/Group%201973770817.svg';
const primeLogo = 'https://res.insta360.com/static/c690c3ff8731085dae0592abaeb07e93/BWP_LOGO.svg';
const primeTagIcon = 'https://res.insta360.com/static/4ab0b6874a982da9cf12ff46d583ff7b/Frame%201973771725.svg';

// const MatterPortCid = 2952; // 测试
const MatterPortCid = 2935; // 线上

const TEXT_COLOR = '#191C21';
const LINK_COLOR = '#31A5FF';

/** 营销模块入口，
 * 展示折扣信息：活动赠品、分销赠品、其他折扣信息、以旧换新、专业级升级福利
 *  */
const ProductDiscount = inject(
  'app',
  'area',
  'product',
  'abtest'
)(
  observer((props) => {
    const {
      productDetail,
      curSelectCommodity,
      productGiftsMap,
      productDetail: { id: pid },
      curSelectCommodity: { id: cid },
    } = props.product;
    const { campaignWrap, showUDesk, insrc, pageCommonConfig, isMobile } = props.app;
    const {
      LANG,
      country,
      country: { code },
    } = props.area;
    const { isInVWOabtNotShowPrime } = props.abtest;
    const { visible } = useWatch({ dom: '[data-click-event=showLearnModal]', once: true });

    /** 是否展示Prime消息 */
    const [isPrimeMessageVisible, setIsPrimeMessageVisible] = useState(true);

    useEffect(() => {
      if (visible) {
        ProductTracker.productPageBindService({
          action: 'care_promotion_show',
          productState: props.product,
        });
      }
    }, [visible]);

    // 垂类活动信息或者其他营销信息（置顶和置底）
    const [discountInfosTop, discountInfosBottom] = useMemo(() => {
      const activityInfos = campaignWrap?.getCampaignDiscountInfo({
        pid,
        cid,
        productDetail,
        curSelectCommodity,
      });
      // 活动倒计时插入在垂类活动信息或者其他营销信息中
      activityInfos?.forEach((infos) => {
        if (Array.isArray(infos)) {
          infos.forEach((info, index) => {
            const text = (typeof info === 'string' && !!info) || info.html || info.text;
            // 只有第一个信息才有倒计时模块
            if (!isEmpty(text) && index === 0) {
              info.html = (
                <div style={{ whiteSpace: 'pre-line' }}>
                  {text}
                  {info?.isLastWeek && <CountDownProduct />}
                </div>
              );
            }
          });
        }
      });

      // oneRS针对部分分销商折扣信息
      const getOneRsAffiliateTip = () => {
        if (pid !== 15) return null;
        if (!['INRVM9S', 'INRGBD9', 'INRC0A3', 'INRUEYT', 'INREYJ2', 'INRWJVF', 'INREB1B'].includes(insrc)) return null;
        const currencyMap = {
          HKD: 240,
          AUD: 40,
          JPY: 3800,
          EUR: 28,
          GBP: 23,
          USD: 30,
          CAD: 38,
          KRW: 37000,
          TWD: 880,
          SGD: 40,
        };
        return LANG['product.page.oners_affiliate_tips']?.replace(
          '_price_',
          `<span style='color:#fe5656;font-weight: bold'>${formatPriceThousands({
            price: currencyMap[country?.currency],
            country,
          })}</span>`
        );
      };

      // Ace特殊维修服务信息
      const aceRepairTip = {
        text: pid !== 25 ? '' : LANG['product.Ace.AceRepaireInfo'],
        icon: DISCOUNT_ICON[1],
      };

      // Go3s点击单相机埋点
      const handleBuyNowGo3sMachine = () => {
        ProductTracker.productPage({
          action: 'click_go3s_standalone_camera',
          label: productDetail.categoryKey,
          productState: props.product,
        });
      };

      // Go3s特殊配置
      const go3sToMachine = {
        html:
          pid !== 27 ? null : (
            <div>
              {LANG['product.go3s.go3s_to_machine']}
              <a
                style={{
                  color: '#31a5ff',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
                onClick={handleBuyNowGo3sMachine}
                href='/product/go-3s-standalone-camera?from=go-3s'
                target='_blank'
                rel='noopener noreferrer'
              >
                {LANG['common.btn.buyNow']}
              </a>
            </div>
          ),
        icon: DISCOUNT_ICON[4],
      };

      // X4 Peter McKinnon Edition, 1736262000000 2025/01/07 23:00 启用
      // 2025.02.20 正式下线
      // eslint-disable-next-line no-unused-vars
      const X4PMSellingPoint = {
        html:
          pid !== 26 || Date.now() < 1736262000000 ? null : (
            <div
              style={{
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '18px',
                color: TEXT_COLOR,
              }}
            >
              <div>{LANG['productNew.x4_pm_title']}</div>
              <div
                style={{
                  marginTop: '2px',
                }}
              >
                {LANG['productNew.x4_pm_edition']}&nbsp;
                <a
                  className={styles.customerService}
                  href='https://store.insta360.com/product/x4?c=3691'
                  alt='Pro Bundle (Peter McKinnon Edition)'
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    color: LINK_COLOR,
                    cursor: 'pointer',
                  }}
                >
                  {LANG['productNew.learn_more']}
                </a>
              </div>
              {['US', 'CA'].includes(code) && (
                <div
                  className={styles.smallDeviceFlexColumn}
                  style={{
                    marginTop: '2px',
                  }}
                >
                  Peter McKinnon Limited Edition Bundle:&nbsp;
                  <a
                    className={styles.customerService}
                    href='https://store.insta360.com/product/x4?c=3692'
                    alt='Peter McKinnon Limited Edition Bundle'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      color: LINK_COLOR,
                      cursor: 'pointer',
                    }}
                  >
                    {LANG['productNew.learn_more']}
                  </a>
                </div>
              )}
            </div>
          ),
        icon: X4_PM_BUNDLE_CAMERA,
      };

      /** 是否展示Prime营销, x3, x4, x5, ace pro, ace pro2展示 */
      const primeEnable = [25, 26, 19, 30, 33].includes(pid) && code === 'US';
      // prime营销展示，根据abt变更
      const primeDiscount = !isInVWOabtNotShowPrime &&
        primeEnable && {
          html: <PrimeDiscountText isMobile={isMobile} />,
          icon: primeLogo,
        };

      // prime消息
      const primeMessage = primeEnable && {
        html: <PrimeDeliveryText onVisibleChange={setIsPrimeMessageVisible} />,
        icon: primeTagIcon,
      };

      const primeInfo = isPrimeMessageVisible ? primeMessage : primeDiscount;

      // 房产利益点：显示逻辑 1. 所选的套餐中有分销赠品为Matteport 2. 在活动期间
      const { matterPortEndTime = 0 } = pageCommonConfig;
      const matterPortGiftsInfo = [];
      const now = new Date().getTime();
      const c1 = now < matterPortEndTime;
      const c2 = productGiftsMap[cid]?.resellerGift?.some((item) => item.commodityId === MatterPortCid);
      if (c1 && c2) {
        matterPortGiftsInfo.push({ icon: DISCOUNT_ICON[0], text: LANG['productNew.matterPortDiscountIntro'] });
      }

      const infosTop = [primeInfo, ...(activityInfos?.[0] || [])];
      const infosBottom = [
        ...matterPortGiftsInfo,
        getOneRsAffiliateTip(),
        ...(activityInfos?.[1] || []),
        aceRepairTip,
        go3sToMachine,
      ];

      return [infosTop, infosBottom];
    }, [cid, productGiftsMap, isInVWOabtNotShowPrime, isPrimeMessageVisible]);

    // 活动赠品信息
    const activityGifts = useMemo(() => {
      let gifts = productGiftsMap[cid]?.activityGift;
      if (isEmpty(gifts)) return null;

      // 活动期间隐藏特定赠品文案
      Object.values(props.app.campaignWrap.campaignWrap).forEach((_campaign) => {
        const hideGift = _campaign.hideGiftIntro?.(pid);
        if (_campaign.enabled && hideGift) {
          gifts = gifts?.filter((el) => !hideGift?.giftCids.includes(el.commodityId));
        }
      });

      // 大促期间，美国地区从活动赠品中隐藏care，单独展示在活动文案上
      const { Q4BFGiftsCareForUS } = props.app.campaignWrap.campaignWrap;
      gifts = Q4BFGiftsCareForUS.filterCareFromProductActivityGifts({ activityGifts: gifts });

      return {
        icon: DISCOUNT_ICON[0],
        html: getGiftsIntro(gifts, 'activityGift', { LANG, country }),
      };
    }, [cid, productGiftsMap]);

    // 活动折扣信息(或者其他营销信息)-活动赠品之后-分销赠品之前
    const infosAfterActivityGifts = useMemo(() => {
      const activityInfos = campaignWrap?.getCampaignDiscountInfoAfterActivity({
        pid,
        cid,
        productDetail,
        curSelectCommodity,
      });
      const infos = [...(activityInfos || [])];

      // 如果是carePlus产品才显示
      const serviceInfo = ExtraBindServiceConfig.getServiceInfo(pid) || {};
      if (serviceInfo?.serviceType === 'care_plus') {
        infos.push({
          icon: DISCOUNT_ICON[1],
          className: styles.carePlusInfo,
          html: (
            <div>
              {serviceInfo.carePlusProductPage.map((o) => {
                return <li key={o}>{o}</li>;
              })}
            </div>
          ),
        });
      }

      return infos;
    }, [cid]);

    // 分销赠品信息
    const affiliateGifts = useMemo(() => {
      const noShowCid = [MatterPortCid];
      const gifts = productGiftsMap[cid]?.resellerGift?.filter((o) => !noShowCid.includes(o.commodityId));
      const redirectData = ThirdPartyPlatformConfig.getRedirectData({ pid, cid });
      if (isEmpty(gifts) || redirectData?.text) return null;

      return {
        icon: DISCOUNT_ICON[0],
        html: getGiftsIntro(gifts, 'resellerGift', { LANG, country, pid, cid, countryCode: code, promoCode: insrc }),
      };
    }, [cid, productGiftsMap]);

    const isEmptyInfo = (infos) => {
      return (Array.isArray(infos) ? infos : typeof infos === 'object' ? [infos] : []).every(
        (d) => !d || isEmpty(typeof d === 'object' ? d?.text || d?.html : d, true)
      );
    };

    // 判断组件模块是否为空
    const EnabledComponent = useCallback(
      (key) => {
        switch (key) {
          case 'ProfessionalDiscountEntry':
            if (
              ProfessionalDiscountConfig.isEnableProduct({ LANG, pid }) &&
              !campaignWrap?.enableCampaigns({ pid, cid, productDetail, curSelectCommodity }, 'professional_discount')
            ) {
              return <ProfessionalDiscountEntry className={styles.item} />;
            }
            break;
          // case 'TradeUpEntry':
          //   if (
          //     enableProductPage(code, pid) &&
          //     !campaignWrap?.enableCampaigns({ pid, cid, productDetail, curSelectCommodity }, 'tradeup')
          //   ) {
          //     return <TradeupEntry className={styles.item} />;
          //   }
          //   break;
          default:
            return null;
        }

        return false;
      },
      [pid, cid, discountInfosTop, discountInfosBottom]
    );

    const customerService = useCustomerService({
      area: props.area,
      showUDesk,
      pid,
      abtest: props.abtest,
    });

    // 实时查看当前的discount下有没有内容，如没有则模块不显示。
    const enable = useMemo(() => {
      return (
        !!EnabledComponent('ProfessionalDiscountEntry') ||
        !isEmptyInfo(discountInfosTop) ||
        !isEmptyInfo(discountInfosBottom) ||
        !isEmptyInfo(activityGifts) ||
        !isEmptyInfo(affiliateGifts) ||
        !isEmptyInfo(infosAfterActivityGifts) ||
        !isEmptyInfo(customerService)
        // !!EnabledComponent('TradeUpEntry')
      );
    }, [
      EnabledComponent,
      discountInfosTop,
      discountInfosBottom,
      activityGifts,
      affiliateGifts,
      infosAfterActivityGifts,
      customerService,
    ]);

    /**
     * @param {Object | Array} infos
     * @param icon
     * @param text
     * @param html
     * @param className 最外层的class
     */
    const InfoItem = useCallback((infos = []) => {
      try {
        const _infos = (Array.isArray(infos) ? infos : typeof infos === 'object' ? [infos] : []).filter(
          (d) => !isEmpty(typeof d === 'object' ? d?.text || d?.html : d, true)
        );
        if (isEmpty(_infos)) return null;
        return _infos?.map((info, i) => {
          return (
            <div className={[styles.item, info.className || ''].join(' ')} key={i}>
              <div className={styles.icon}>
                <img alt='' src={info?.icon ?? DISCOUNT_ICON[2]} />
              </div>
              {info?.html ? (
                info?.renderHtml ? (
                  <div>
                    <div
                      className={styles.text}
                      dangerouslySetInnerHTML={{ __html: info?.text }}
                      style={{ whiteSpace: 'pre-line' }}
                    />
                    {/* 特殊文案手动插入倒计时 */}
                    {info?.isLastWeek && <CountDownProduct />}
                  </div>
                ) : (
                  <div className={styles.text}>{info?.html}</div>
                )
              ) : (
                <div className={styles.text} dangerouslySetInnerHTML={{ __html: info?.text ?? info }} />
              )}
            </div>
          );
        });
      } catch (e) {
        armsErrorSend(e, { filename: 'productDiscount infoItem', data: infos });
        return null;
      }
    }, []);

    // eslint-disable-next-line no-unused-vars
    const handleShowBindServiceLearnMore = (e) => {
      if (e.target?.dataset?.clickEvent === 'showLearnModal') {
        const careExtraBindService = props.product.extraBindService?.find((o) => o.extraType?.includes('care'));
        props?.product?.BindServiceLearnMoreModalRef?.onShow({
          service: careExtraBindService?.commodity,
          showFrom: 'productDiscount',
        });
      }
    };

    return (
      <div className={styles.discount} data-hide={`${!enable}`} onClick={handleShowBindServiceLearnMore}>
        <div className={styles.content}>
          {/* 置顶-活动折扣信息、其他折扣信息 */}
          {InfoItem(discountInfosTop)}

          {/* 活动赠品 */}
          {InfoItem(activityGifts)}

          {/* 活动折扣信息 - 活动赠品之后 */}
          {InfoItem(infosAfterActivityGifts)}

          {/* 分销赠品 */}
          {InfoItem(affiliateGifts)}

          {/* 活动折扣信息、其他折扣信息 */}
          {InfoItem(discountInfosBottom)}

          {/* 以旧换新入口 */}
          {/* 下线以旧换新入口  */}
          {/* {EnabledComponent('TradeUpEntry')} */}

          {/* 专业级相机升级福利入口 */}
          {EnabledComponent('ProfessionalDiscountEntry')}

          {InfoItem(customerService)}
        </div>
      </div>
    );
  })
);

export default ProductDiscount;
